import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const SKIP_KEY = 'standor_intro_v1';
const EASE = [0.22, 1, 0.36, 1];

// ─── SVG Trophy ──────────────────────────────────────────────────────────────
function Trophy() {
  return (
    <svg
      viewBox="0 0 100 150"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ width: '100%', height: '100%' }}
    >
      {/* Glow behind cup */}
      <ellipse cx="50" cy="48" rx="28" ry="26" fill="rgba(201,169,97,0.06)" />

      {/* Cup bowl */}
      <path
        d="M20 12 H80 L71 70 Q50 88 29 70 Z"
        stroke="#C9A961"
        strokeWidth="1.4"
        strokeLinejoin="round"
        fill="rgba(201,169,97,0.03)"
      />

      {/* Left handle */}
      <path
        d="M20 12 C5 12 5 44 20 50"
        stroke="#C9A961"
        strokeWidth="1.4"
        fill="none"
      />
      {/* Right handle */}
      <path
        d="M80 12 C95 12 95 44 80 50"
        stroke="#C9A961"
        strokeWidth="1.4"
        fill="none"
      />

      {/* Inner lip highlight */}
      <path
        d="M24 18 H76"
        stroke="rgba(201,169,97,0.25)"
        strokeWidth="0.8"
      />

      {/* Star in cup */}
      <polygon
        points="50,28 53,38 63,38 55,44 58,54 50,48 42,54 45,44 37,38 47,38"
        fill="#C9A961"
        opacity="0.75"
      />

      {/* Stem */}
      <line
        x1="50" y1="88"
        x2="50" y2="114"
        stroke="#C9A961"
        strokeWidth="1.4"
      />
      {/* Knob */}
      <circle cx="50" cy="100" r="3" fill="none" stroke="#C9A961" strokeWidth="1" />

      {/* Base tier 1 */}
      <rect
        x="31" y="114"
        width="38" height="6"
        rx="1"
        stroke="#C9A961"
        strokeWidth="1.2"
        fill="rgba(201,169,97,0.04)"
      />
      {/* Base tier 2 */}
      <rect
        x="22" y="122"
        width="56" height="7"
        rx="1"
        stroke="#C9A961"
        strokeWidth="1.2"
        fill="rgba(201,169,97,0.04)"
      />

      {/* Bottom glow line */}
      <line
        x1="22" y1="130"
        x2="78" y2="130"
        stroke="rgba(201,169,97,0.18)"
        strokeWidth="0.6"
      />
    </svg>
  );
}

// ─── Individual engraved letter ───────────────────────────────────────────────
function Letter({ char, delay }) {
  if (char === ' ') {
    return <span style={{ display: 'inline-block', width: '0.4em' }} aria-hidden="true" />;
  }
  return (
    <motion.span
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: EASE }}
      style={{ display: 'inline-block' }}
      aria-hidden="true"
    >
      {char}
    </motion.span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function IntroAnimation() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(true);
  const [contentReady, setContentReady] = useState(false);

  const goHome = (replace = true) => {
    sessionStorage.setItem(SKIP_KEY, '1');
    setVisible(false);
    // AnimatePresence handles the fade; navigate inside onExitComplete
  };

  useEffect(() => {
    const seen = sessionStorage.getItem(SKIP_KEY);
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (seen || prefersReduced) {
      navigate('/home', { replace: true });
      return;
    }

    // Show trophy + name once curtains are open enough
    const t1 = setTimeout(() => setContentReady(true), 1200);

    // Start exit at 4 s
    const t2 = setTimeout(() => {
      sessionStorage.setItem(SKIP_KEY, '1');
      setVisible(false);
    }, 4000);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const title = "LE STAND D'OR";
  const letters = title.split('');

  return (
    <AnimatePresence onExitComplete={() => navigate('/home', { replace: true })}>
      {visible && (
        <motion.div
          key="intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: 'easeInOut' }}
          style={{
            position: 'fixed',
            inset: 0,
            background: '#0A0908',
            zIndex: 9999,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* ── Film grain overlay ── */}
          <div
            className="grain"
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}
          />

          {/* ── Vignette ── */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.7) 100%)',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />

          {/* ── LEFT CURTAIN ── */}
          <motion.div
            aria-hidden="true"
            initial={{ x: 0 }}
            animate={{ x: '-101%' }}
            transition={{ duration: 1.4, delay: 0.2, ease: EASE }}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '50%',
              height: '100%',
              background:
                'linear-gradient(to right, #060504 0%, #0A0908 80%, rgba(201,169,97,0.08) 100%)',
              borderRight: '1px solid rgba(201,169,97,0.18)',
              zIndex: 20,
            }}
          />

          {/* ── RIGHT CURTAIN ── */}
          <motion.div
            aria-hidden="true"
            initial={{ x: 0 }}
            animate={{ x: '101%' }}
            transition={{ duration: 1.4, delay: 0.2, ease: EASE }}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: '50%',
              height: '100%',
              background:
                'linear-gradient(to left, #060504 0%, #0A0908 80%, rgba(201,169,97,0.08) 100%)',
              borderLeft: '1px solid rgba(201,169,97,0.18)',
              zIndex: 20,
            }}
          />

          {/* ── CENTER STAGE ── */}
          {contentReady && (
            <div
              style={{
                position: 'relative',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0,
                textAlign: 'center',
              }}
            >
              {/* Trophy */}
              <motion.div
                initial={{ opacity: 0, y: 28, scale: 0.82 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.0, ease: EASE }}
                style={{ width: 72, height: 108, marginBottom: 32 }}
              >
                <Trophy />
              </motion.div>

              {/* Gold spotlight ray below trophy */}
              <motion.div
                aria-hidden="true"
                initial={{ opacity: 0, scaleY: 0 }}
                animate={{ opacity: 1, scaleY: 1 }}
                transition={{ duration: 0.7, delay: 0.4, ease: EASE }}
                style={{
                  position: 'absolute',
                  top: 100,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 1,
                  height: 48,
                  background:
                    'linear-gradient(to bottom, rgba(201,169,97,0.45), transparent)',
                  transformOrigin: 'top',
                }}
              />

              {/* Horizontal hairline */}
              <motion.div
                aria-hidden="true"
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
                style={{
                  width: 100,
                  height: '1px',
                  background:
                    'linear-gradient(to right, transparent, rgba(201,169,97,0.6), transparent)',
                  marginBottom: 24,
                  transformOrigin: 'center',
                }}
              />

              {/* Title — engraved letter by letter */}
              <div aria-label="Le Stand d'Or">
                <p
                  className="font-serif"
                  style={{
                    fontSize: 'clamp(22px, 3.5vw, 48px)',
                    letterSpacing: '0.3em',
                    color: '#F5F1E8',
                    lineHeight: 1,
                    marginBottom: 0,
                  }}
                >
                  {letters.map((char, i) => (
                    <Letter key={i} char={char} delay={0.4 + i * 0.055} />
                  ))}
                </p>
              </div>

              {/* Edition label */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.9 }}
                className="label"
                style={{
                  color: 'rgba(201,169,97,0.55)',
                  letterSpacing: '0.3em',
                  fontSize: 9,
                  marginTop: 20,
                }}
              >
                ÉDITION MMXXVI
              </motion.p>
            </div>
          )}

          {/* ── Skip button ── */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            onClick={() => goHome()}
            className="label"
            style={{
              position: 'absolute',
              bottom: 36,
              right: 44,
              background: 'none',
              border: 'none',
              color: 'rgba(248,246,240,0.25)',
              cursor: 'pointer',
              fontSize: 10,
              letterSpacing: '0.22em',
              zIndex: 30,
              padding: '8px 0',
              transition: 'color 0.3s ease',
            }}
            onMouseOver={(e) => (e.currentTarget.style.color = 'rgba(201,169,97,0.65)')}
            onMouseOut={(e) => (e.currentTarget.style.color = 'rgba(248,246,240,0.25)')}
            aria-label="Passer l'introduction"
          >
            PASSER L'INTRO —
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
